import type { FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DateInput,
  formatTimeZoneLabel,
  joinDateTime,
  splitDateTime,
  TimeInput,
} from '../DateInputs';
import { Select, type SelectOption } from '../Select';
import { SelectField } from './ChoiceFields';
import { FormField } from './FormField';
import { asText, type BaseFieldProps, describedBy, useBoundField, useFieldIds } from './fieldBase';

// ——— date / datetime / timezone ———

export function DateField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
}: BaseFieldProps<TValues>) {
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const error = fieldState.error?.message;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <DateInput
        id={ids.id}
        ref={field.ref}
        name={field.name}
        value={asText(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        disabled={disabled || field.disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
      />
    </FormField>
  );
}

function timeZoneOptionsOf(timeZones: readonly string[]): SelectOption[] {
  return timeZones.map((zone) => ({ value: zone, label: formatTimeZoneLabel(zone) }));
}

export interface DateTimeFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  /** Поле формы с IANA-поясом события (если пояс выбирается рядом с датой). */
  timeZoneName?: FieldPath<TValues>;
  /** Допустимые пояса (MarketConfig `exp.event_timezones` / `time.admin_timezones`). */
  timeZones?: readonly string[];
  /** Пояса с подписями сервера («Москва · UTC+3»): заменяют `timeZones` с подписями Intl. */
  timeZoneOptions?: readonly SelectOption[];
}

/** Дата и время `YYYY-MM-DDTHH:mm` (локальное время события) + пояс отдельным полем (ТЗ COM-10). */
export function DateTimeField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide = true,
  disabled,
  timeZoneName,
  timeZones = [],
  timeZoneOptions,
}: DateTimeFieldProps<TValues>) {
  const { t } = useTranslation('form');
  const ids = useFieldIds();
  const { field, fieldState } = useBoundField<TValues>(name);
  const parts = splitDateTime(asText(field.value));
  const error = fieldState.error?.message;
  const isDisabled = disabled || field.disabled;
  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <div className="flex min-w-0 flex-wrap gap-8">
        <DateInput
          id={ids.id}
          ref={field.ref}
          className="min-w-0 flex-[1_1_130px]"
          value={parts.date}
          onChange={(event) => field.onChange(joinDateTime({ ...parts, date: event.target.value }))}
          onBlur={field.onBlur}
          disabled={isDisabled}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(Boolean(error), Boolean(hint), ids)}
        />
        <TimeInput
          value={parts.time}
          onChange={(event) => field.onChange(joinDateTime({ ...parts, time: event.target.value }))}
          onBlur={field.onBlur}
          disabled={isDisabled}
          aria-label={t('date.time')}
          aria-invalid={error ? true : undefined}
        />
        {timeZoneName ? (
          <TimeZoneSelect
            name={timeZoneName}
            options={timeZoneOptions ?? timeZoneOptionsOf(timeZones)}
            disabled={isDisabled}
          />
        ) : null}
      </div>
    </FormField>
  );
}

function TimeZoneSelect<TValues extends FieldValues>({
  name,
  options,
  disabled,
}: {
  name: FieldPath<TValues>;
  options: readonly SelectOption[];
  disabled?: boolean;
}) {
  const { t } = useTranslation('form');
  const { field, fieldState } = useBoundField<TValues>(name);
  return (
    <Select
      className="min-w-0 flex-[1_1_180px]"
      value={asText(field.value)}
      onValueChange={field.onChange}
      onBlur={field.onBlur}
      options={options}
      disabled={disabled}
      invalid={Boolean(fieldState.error)}
      aria-label={t('date.timezone')}
    />
  );
}

export interface TimezoneFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  timeZones: readonly string[];
}

export function TimezoneField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide,
  disabled,
  timeZones,
}: TimezoneFieldProps<TValues>) {
  return (
    <SelectField
      name={name}
      label={label}
      required={required}
      hint={hint}
      wide={wide}
      disabled={disabled}
      options={timeZoneOptionsOf(timeZones)}
    />
  );
}
